// «Справочник радиоинженера» — методичка портала 73 на экране CRM. ТОЛЬКО ЧТЕНИЕ.
//
// ЧТО ЭТО. 159 разделов в 16 главах: нормативка ГМССБ, АКБ, АРБ, РДР, ЭКНИС, РЛС,
// гирокомпасы, ГНСС, АИС, Inmarsat/VSAT, ПВ/КВ/УКВ, кабели, инструкции по монтажу, ТО,
// освидетельствование, справочные таблицы. Пользуются ~20 инженеров. Источник —
// статический handbook.html портала 73; в зеркало его разово переносит sync_p73ref.py
// со слепком версии. На портале эта страница отдаётся АНОНИМУ вместе с сервисными
// паролями оборудования (задача #64) — здесь она за логином, и это половина смысла
// раздела.
//
// ГЛАВНЫЙ ВОПРОС ЭКРАНА: «как обслуживать вот эту модель?». Поэтому наверху — поиск,
// под ним оглавление главами; раздел открывается на этом же экране, с листанием
// вперёд-назад по книге.
//
// ПРО dangerouslySetInnerHTML — ЕДИНСТВЕННОЕ МЕСТО, ГДЕ ЭКРАН ВСТАВЛЯЕТ ГОТОВЫЙ HTML.
// Тело раздела приезжает разметкой источника (таблицы на сотни строк, картинки), и
// пересобирать её в JSX значило бы переписать вёрстку методички. Это безопасно не
// «потому что свои»: sync_p73ref.py ПАДАЕТ на переносе, если в теле раздела есть
// <script>, <style>, on*-атрибут или javascript: (в v5 их ноль, проверено разбором),
// то есть в базе исполняемой разметки не бывает. Картинки в html стоят заглушками
// data-media="imageN.png" и подставляются отдельными запросами — см. ТелоРаздела.
//
// ТРАНСПОРТ ЛЕЖИТ В ЭТОМ ЖЕ ФАЙЛЕ, а не в api/p73ref.ts, — и это ограничение задачи,
// а не находка: раздел писался параллельной сессией, которой из общих файлов не выдано
// ни одного, а «один файл — один владелец». Захочет основная сессия — вынесет типы и
// хуки в api/ одним движением, форма у них ровно та же, что в api/p73i.ts.
//
// Пути ядра: GET /p73ref/refs, /p73ref/section?slug=, /p73ref/search?q=,
// /p73ref/media?name= (b64, как у фотографий p73 — см. api/p73.ts:адресФайла).
import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { Unauthorized } from '../api/client'
import { QueryState } from '../components/QueryState'
import { fmtDate, pluralN } from '../lib/format'

const БАЗА = '/api/p73ref'

async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 403) {
      throw new Error('раздел закрыт вашей роли — какой группе он открыт, решает владелец')
    }
    // hint раньше error: в hint ядро кладёт человеческое «такого раздела нет…»,
    // а в error — голое «not found».
    const msg = (rec.why as string) || (rec.hint as string) || (rec.error as string)
      || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────── формы ответов ядра (см. p73ref_api.py) ───────────

/** Строка оглавления: один раздел книги. */
interface ПунктОглавления {
  slug: string
  position: number
  grp_num: number | null
  grp_title: string | null
  title: string
  /** В тексте упомянуты сервисные пароли оборудования (29 разделов v5). */
  has_password: boolean
  media_n: number
  tables_n: number
}

interface СчётчикиP73Ref {
  sections: number
  groups: number
  with_password: number
  tables: number
  media: number
  media_bytes: number
  gone: number
}

interface СвежестьP73Ref {
  entity: string
  source_sha: string | null
  last_full: string | null
  last_count: number | null
  last_sync_at: string | null
}

interface СправочникиP73Ref {
  rows: ПунктОглавления[]
  total: number
  counts: СчётчикиP73Ref
  freshness: СвежестьP73Ref[]
  /** Оговорки о данных — со вставленными ядром числами. Печатаются как есть. */
  caveats: string[]
  source: string
}

interface РазделКнига {
  slug: string
  position: number
  grp_num: number | null
  grp_title: string | null
  title: string
  html: string
  has_password: boolean
  media: string[]
  tables_n: number
  source_sha: string
  imported_at: string | null
  prev: { slug: string; title: string } | null
  next: { slug: string; title: string } | null
}

interface Находка {
  slug: string
  position: number
  grp_num: number | null
  grp_title: string | null
  title: string
  has_password: boolean
  rank: number
  /** Выдержка ts_headline: попадания обёрнуты <b>…</b>. Рисуется РАЗБОРОМ, не innerHTML. */
  snippet: string
  /** Чем найдено: 'fts' — по словоформам, 'ilike' — подстрокой (модели, артикулы). */
  via: 'fts' | 'ilike'
}

interface Поиск {
  rows: Находка[]
  total: number
  q: string
  limit: number
}

const ОБЩЕЕ = { staleTime: 300_000, retry: 1 } as const

function useОглавление() {
  return useQuery<СправочникиP73Ref>({
    queryKey: ['p73ref', 'refs'],
    queryFn: () => запрос<СправочникиP73Ref>('/refs'),
    ...ОБЩЕЕ,
  })
}

function useРаздел(slug: string) {
  return useQuery<РазделКнига>({
    queryKey: ['p73ref', 'section', slug],
    queryFn: () => запрос<РазделКнига>(`/section?slug=${encodeURIComponent(slug)}`),
    enabled: !!slug,
    ...ОБЩЕЕ,
  })
}

function useПоиск(q: string) {
  return useQuery<Поиск>({
    queryKey: ['p73ref', 'search', q],
    queryFn: () => запрос<Поиск>(`/search?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 2,
    ...ОБЩЕЕ,
  })
}

// ─────────── картинки: заглушка data-media -> data:URI ───────────
//
// Кэш на модуль, а не на компонент: человек листает разделы туда-сюда, и повторно
// открытая схема не должна ехать через ядро второй раз. Кэшируется ОБЕЩАНИЕ, а не
// результат: две одинаковые картинки в одном разделе иначе поехали бы двумя запросами.
const кэшКартинок = new Map<string, Promise<string>>()

function адресКартинки(имя: string): Promise<string> {
  let p = кэшКартинок.get(имя)
  if (!p) {
    p = запрос<{ file: { content_type: string; b64: string } }>(
      `/media?name=${encodeURIComponent(имя)}`,
    ).then((т) => `data:${т.file.content_type};base64,${т.file.b64}`)
    // Ошибку из кэша убираем: иначе один сетевой сбой навсегда оставил бы дырку.
    p.catch(() => кэшКартинок.delete(имя))
    кэшКартинок.set(имя, p)
  }
  return p
}

/** Тело раздела: html источника + подстановка картинок по data-media. */
function ТелоРаздела({ html }: { html: string }) {
  const узелRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const узел = узелRef.current
    if (!узел) return undefined
    let живой = true
    узел.querySelectorAll('img[data-media]').forEach((img) => {
      const имя = img.getAttribute('data-media') ?? ''
      адресКартинки(имя)
        .then((src) => {
          if (живой) img.setAttribute('src', src)
        })
        .catch(() => {
          // Смолчать нельзя: пустой прямоугольник читается как «схемы нет вовсе».
          if (живой) img.setAttribute('alt', `картинка ${имя} не загрузилась — обновите страницу`)
        })
    })
    return () => {
      живой = false
    }
  }, [html])
  return (
    <div
      ref={узелRef}
      className="radioref-body"
      // Почему это безопасно — в шапке файла: синк не пропускает исполняемую разметку.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

/** Выдержка поиска: <b>…</b> от ts_headline рисуется разбором, а не innerHTML. */
function Выдержка({ snippet }: { snippet: string }) {
  const куски = snippet.split(/<\/?b>/)
  return (
    <span className="hint">
      {куски.map((к, i) => (i % 2 ? <mark key={i}>{к}</mark> : <span key={i}>{к}</span>))}
    </span>
  )
}

/** Значки раздела: пароли, схемы, таблицы. Одни и те же в оглавлении и в поиске. */
function Значки({ п }: { п: { has_password: boolean; media_n?: number; tables_n?: number } }) {
  return (
    <>
      {п.has_password && (
        <span className="badge" title="в тексте есть сервисные пароли оборудования">пароли</span>
      )}
      {(п.media_n ?? 0) > 0 && (
        <span className="badge">{pluralN(п.media_n ?? 0, 'схема', 'схемы', 'схем')}</span>
      )}
      {(п.tables_n ?? 0) > 0 && (
        <span className="badge">{pluralN(п.tables_n ?? 0, 'таблица', 'таблицы', 'таблиц')}</span>
      )}
    </>
  )
}

// Стили тела раздела. Здесь, а не в styles.css: styles.css — общий файл, а разметка
// внутри .radioref-body — чужая (портальная: .tbl-wrap, table.cards, data-label), и её
// правила не должны утекать на остальные экраны. Всё заперто под .radioref-body.
const СТИЛИ = `
.radioref-body { line-height: 1.55; overflow-wrap: anywhere; }
.radioref-body img { max-width: 100%; height: auto; border-radius: var(--r1); }
.radioref-body .tbl-wrap { overflow-x: auto; margin: var(--s3) 0; }
.radioref-body table { border-collapse: collapse; font-size: 14px; }
.radioref-body th, .radioref-body td {
  border: 1px solid var(--border); padding: var(--s1) var(--s2); vertical-align: top;
}
.radioref-body th { background: var(--surface-2); text-align: left; }
.radioref-body h3, .radioref-body h4 { margin: var(--s4) 0 var(--s2); }
@media (max-width: 700px) {
  /* Мобильная вёрстка таблиц источника: rebuild.py портала пометил узкие таблицы
     классом cards и подписал ячейки data-label — включаем то же поведение. */
  .radioref-body table.cards, .radioref-body table.cards tbody,
  .radioref-body table.cards tr, .radioref-body table.cards td { display: block; width: 100%; }
  .radioref-body table.cards thead { display: none; }
  .radioref-body table.cards tr { border: 1px solid var(--border); margin-bottom: var(--s2); }
  .radioref-body table.cards td { border: 0; border-bottom: 1px solid var(--border); }
  .radioref-body table.cards td[data-label]::before {
    content: attr(data-label); display: block; color: var(--muted); font-size: 12px;
  }
}
`

export default function RadioRef() {
  const [параметры, setПараметры] = useSearchParams()
  const slug = параметры.get('sec') ?? ''
  const [q, setQ] = useState(параметры.get('q') ?? '')

  const открыть = (s: string) => {
    setПараметры(s ? { sec: s } : {}, { replace: false })
  }

  return (
    <div>
      <style>{СТИЛИ}</style>
      <div className="page-head">
        <h1>Справочник радиоинженера</h1>
      </div>
      {slug
        ? <Раздел slug={slug} открыть={открыть} />
        : <Оглавление q={q} setQ={setQ} открыть={открыть} />}
    </div>
  )
}

// ═══════════════════════════════ оглавление и поиск ═══════════════════════════════

function Оглавление({ q, setQ, открыть }: {
  q: string
  setQ: (v: string) => void
  открыть: (slug: string) => void
}) {
  const refs = useОглавление()
  const ищем = q.trim().length >= 2
  const поиск = useПоиск(q.trim())
  const счёт = refs.data?.counts
  const слепок = refs.data?.freshness.find((f) => f.entity === 'p73ref:sections')

  return (
    <QueryState isLoading={refs.isLoading} error={refs.error}>
      <div className="card" style={{ marginBottom: 'var(--s3)' }}>
        <label htmlFor="radioref-q" className="hint">
          Поиск по всему тексту и таблицам — модель, раздел, неисправность
        </label>
        <input
          id="radioref-q"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="например: гидростат, FA-170, замена магнетрона"
          style={{ width: '100%', marginTop: 'var(--s1)' }}
        />
        {счёт && (
          <div className="hint" style={{ marginTop: 'var(--s2)' }}>
            {pluralN(счёт.sections, 'раздел', 'раздела', 'разделов')}
            {' в '}
            {pluralN(счёт.groups, 'главе', 'главах', 'главах')}
            {', '}
            {pluralN(счёт.tables, 'таблица', 'таблицы', 'таблиц')}
            {', '}
            {pluralN(счёт.media, 'картинка', 'картинки', 'картинок')}
            {слепок?.source_sha && (
              <>
                {' · слепок версии '}
                <span className="mono">{слепок.source_sha.slice(0, 8)}</span>
                {слепок.last_full ? ` от ${fmtDate(слепок.last_full)}` : ''}
              </>
            )}
          </div>
        )}
      </div>

      {ищем
        ? <Находки поиск={поиск} открыть={открыть} />
        : <Главы refs={refs.data} открыть={открыть} />}

      {!!refs.data?.caveats.length && (
        <div className="card" style={{ marginTop: 'var(--s3)' }}>
          {refs.data.caveats.map((c, i) => (
            <div key={i} className="hint" style={{ marginBottom: 'var(--s1)' }}>{c}</div>
          ))}
        </div>
      )}
    </QueryState>
  )
}

function Главы({ refs, открыть }: {
  refs: СправочникиP73Ref | undefined
  открыть: (slug: string) => void
}) {
  if (!refs) return null
  if (!refs.rows.length) {
    return (
      <div className="card">
        <div className="empty">
          <div className="empty-title">Справочник ещё не перенесён</div>
          <div className="empty-hint">
            Зеркало пусто: schema_p73ref.sql не накачена или sync_p73ref.py ещё не
            прогонялся. Это состояние зеркала, а не «справочника не существует».
          </div>
        </div>
      </div>
    )
  }
  // Главы в порядке книги. Разделы приходят уже отсортированными по position,
  // поэтому группировка — один проход без пересортировки.
  const главы: { заголовок: string; пункты: ПунктОглавления[] }[] = []
  for (const п of refs.rows) {
    const з = п.grp_title ?? 'Без главы'
    if (!главы.length || главы[главы.length - 1].заголовок !== з) {
      главы.push({ заголовок: з, пункты: [] })
    }
    главы[главы.length - 1].пункты.push(п)
  }
  return (
    <>
      {главы.map((г) => (
        <div className="card" key={г.заголовок} style={{ marginBottom: 'var(--s3)' }}>
          <h2 style={{ margin: '0 0 var(--s2)' }}>{г.заголовок}</h2>
          <ul style={{ margin: 0, paddingLeft: 'var(--s5)' }}>
            {г.пункты.map((п) => (
              <li key={п.slug} style={{ marginBottom: 'var(--s1)' }}>
                <a
                  href={`?sec=${encodeURIComponent(п.slug)}`}
                  onClick={(e) => { e.preventDefault(); открыть(п.slug) }}
                >
                  {п.title}
                </a>{' '}
                <Значки п={п} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  )
}

function Находки({ поиск, открыть }: {
  поиск: ReturnType<typeof useПоиск>
  открыть: (slug: string) => void
}) {
  return (
    <QueryState isLoading={поиск.isLoading} error={поиск.error}>
      {поиск.data && (
        поиск.data.rows.length === 0 ? (
          <div className="card">
            <div className="empty">
              <div className="empty-title">Ничего не найдено</div>
              <div className="empty-hint">
                Поиск смотрит в текст и таблицы, но НЕ в содержимое картинок: схема или
                скриншот с этим словом не найдётся. Попробуйте короче — по модели или по
                одному слову — или пройдите главой в оглавлении.
              </div>
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="hint" style={{ marginBottom: 'var(--s2)' }}>
              {pluralN(поиск.data.rows.length, 'раздел', 'раздела', 'разделов')}
              {поиск.data.rows.length >= поиск.data.limit
                ? ` — показаны первые ${поиск.data.limit}, сузьте запрос`
                : ''}
              {поиск.data.rows[0]?.via === 'ilike'
                ? ' · найдено подстрокой: слово не из словаря, так ищутся модели и артикулы'
                : ''}
            </div>
            <ul style={{ margin: 0, paddingLeft: 'var(--s5)' }}>
              {поиск.data.rows.map((н) => (
                <li key={н.slug} style={{ marginBottom: 'var(--s2)' }}>
                  <a
                    href={`?sec=${encodeURIComponent(н.slug)}`}
                    onClick={(e) => { e.preventDefault(); открыть(н.slug) }}
                  >
                    {н.title}
                  </a>{' '}
                  <Значки п={н} />
                  <div className="hint">{н.grp_title}</div>
                  {н.snippet && <div><Выдержка snippet={н.snippet} /></div>}
                </li>
              ))}
            </ul>
          </div>
        )
      )}
    </QueryState>
  )
}

// ═══════════════════════════════ раздел книги ═══════════════════════════════

function Раздел({ slug, открыть }: { slug: string; открыть: (slug: string) => void }) {
  const раздел = useРаздел(slug)
  const р = раздел.data

  // Свежеоткрытый раздел начинается с начала, а не там, куда доскроллили прошлый.
  // scrollTop, а не window.scrollTo: тот в jsdom (vitest) «not implemented» и сорит
  // в stderr каждого прогона, а присваивание там просто тихо ничего не делает.
  useEffect(() => {
    document.documentElement.scrollTop = 0
  }, [slug])

  const листалка = р && (
    <div style={{ display: 'flex', gap: 'var(--s2)', flexWrap: 'wrap', margin: 'var(--s3) 0' }}>
      <button type="button" onClick={() => открыть('')}>← Оглавление</button>
      {р.prev && (
        <button type="button" onClick={() => открыть(р.prev!.slug)} title={р.prev.title}>
          ‹ Предыдущий
        </button>
      )}
      {р.next && (
        <button type="button" onClick={() => открыть(р.next!.slug)} title={р.next.title}>
          Следующий ›
        </button>
      )}
    </div>
  )

  return (
    <QueryState isLoading={раздел.isLoading} error={раздел.error}>
      {р && (
        <>
          {листалка}
          <div className="card">
            <div className="hint">{р.grp_title}</div>
            <h2 style={{ margin: 'var(--s1) 0 var(--s3)' }}>{р.title}</h2>
            {р.has_password && (
              <div className="notice">
                В разделе есть сервисные пароли оборудования. Они — рабочий инструмент
                инженера, но пересылать снимок этой страницы наружу не надо.
              </div>
            )}
            {!р.html.trim() && (
              <div className="empty">
                <div className="empty-title">Раздел в источнике пуст</div>
                <div className="empty-hint">
                  Так в самом справочнике (перенос «как есть») — на портале этот раздел
                  тоже пустой. Наполнить его может только владелец документа.
                </div>
              </div>
            )}
            <ТелоРаздела html={р.html} />
          </div>
          {листалка}
        </>
      )}
    </QueryState>
  )
}
